import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { EventOrigin, Events, track } from "apis/procore";
import { useAppDispatch, useAppSelector, useAppStore } from "app/redux-store-interactions";
import type { Node } from "components/objectTree/types";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { HighlightCollection, useHighlightCollections } from "contexts/highlightCollections";
import { highlightActions, useDispatchHighlighted } from "contexts/highlighted";
import type { ModelTreeNodeData } from "features/modelTree/types";
import { capturePropertyFilter } from "features/properties/slice";
import { ObjectVisibility, selectDefaultVisibility, selectPreIsolationVisibility } from "features/render";
import { flip, isGlSpace } from "features/render/utils";
import { useAbortController } from "hooks/useAbortController";
import useFlyTo from "hooks/useFlyTo";
import IsolateActionIcon from "media/icons/isolate-action.svg?react";
import ZoomToIcon from "media/icons/zoom-to.svg?react";
import {
    captureVisibilitySnapshot,
    isolateObjects,
    selectObjectHierarchy,
    selectObjectHierarchyFromFilteredTree,
    unisolateObjects,
    unisolateObjectsFromXRayMode,
} from "utils/objectActions";

import { useRowSwipe } from "../hooks/useRowSwipe";

export type SwipeableRowProps = {
    node: Node<ModelTreeNodeData>;
    onOpen: (close: () => void) => void;
    onClose?: () => void;
    children: React.ReactNode;
    filteredObjectIds?: Set<number>;
};

export function SwipeableRow({ node, onOpen, onClose, children, filteredObjectIds }: SwipeableRowProps) {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const store = useAppStore();
    const {
        state: { dataAccess, scene },
    } = useExplorerGlobals(true);
    const currentDefaultVisibility = useAppSelector(selectDefaultVisibility);
    const preIsolationVisibility = useAppSelector(selectPreIsolationVisibility);
    const isXRayActive = currentDefaultVisibility === ObjectVisibility.Xray;
    const highlightCollections = useHighlightCollections();
    const dispatchHighlighted = useDispatchHighlighted();
    const [abortController] = useAbortController();
    const [isBusy, setIsBusy] = useState(false);
    const { contentRef, isRevealed, close } = useRowSwipe();
    const flyTo = useFlyTo();

    const isolationCollection = highlightCollections[HighlightCollection.TransparentSelectionBase];
    // X-ray isolation keeps defaultVisibility at Xray and clears TransparentSelectionBase,
    // so keep-set membership cannot be used to detect it — match the context menu pattern.
    const isXRayIsolated = isXRayActive && preIsolationVisibility !== undefined;
    // Plain isolation: this node's id is in the keep-set iff this row was the source of the last
    // swipe-isolate (selectObjectHierarchy includes the folder id). Toolbar/stamp isolation writes
    // geometry ids only, so a folder grouping id is absent and the row shows "Isolate" (re-isolates
    // to this folder) rather than "Unisolate" — intentional, not a gap.
    const isPlainIsolated =
        !isXRayActive && preIsolationVisibility !== undefined && isolationCollection.ids.has(node.data.object.id);
    const showUnisolate = isXRayIsolated || isPlainIsolated;

    const prevRevealedRef = useRef(false);
    useEffect(() => {
        if (isRevealed && !prevRevealedRef.current) {
            onOpen(close);
        } else if (!isRevealed && prevRevealedRef.current) {
            onClose?.();
        }
        prevRevealedRef.current = isRevealed;
    }, [isRevealed, onOpen, onClose, close]);

    const handleIsolateToggle = async (e: React.MouseEvent) => {
        e.stopPropagation();
        if (isBusy) {
            return;
        }
        if (isXRayIsolated) {
            unisolateObjectsFromXRayMode({
                dispatch,
                restoreToVisibility: preIsolationVisibility!,
                propertyFilter: capturePropertyFilter(store.getState()),
            });
            void track(Events.bimObjects.unisolatedFromXray, { origin: EventOrigin.ObjectTree });
            return;
        }
        if (isPlainIsolated) {
            unisolateObjects({
                dispatch,
                isolationIds: isolationCollection.idArr,
                restoreToVisibility: preIsolationVisibility!,
            });
            void track(Events.bimObjects.unisolated, { origin: EventOrigin.ObjectTree });
            return;
        }
        dispatchHighlighted(highlightActions.setIds([]));
        const affectedIds = await (filteredObjectIds
            ? selectObjectHierarchyFromFilteredTree({
                  nodes: [node.data.object],
                  filteredObjectIds,
                  dataAccess,
                  abortSignal: abortController.current.signal,
                  setLoading: setIsBusy,
                  dispatchHighlighted,
              })
            : selectObjectHierarchy({
                  nodes: [node.data.object],
                  dataAccess,
                  abortSignal: abortController.current.signal,
                  setLoading: setIsBusy,
                  dispatchHighlighted,
              }));
        if (affectedIds.length > 0) {
            isolateObjects({
                dispatch,
                isolationIds: affectedIds,
                currentDefaultVisibility: preIsolationVisibility ?? currentDefaultVisibility,
                visibilityBefore: captureVisibilitySnapshot(store.getState()),
                propertyFilter: capturePropertyFilter(store.getState()),
            });
            void track(Events.bimObjects.isolated, { origin: EventOrigin.ObjectTree });
        }
    };

    const handleZoomTo = async (e: React.MouseEvent) => {
        e.stopPropagation();
        if (isBusy) {
            return;
        }
        // Bounds already on the row need no fetch. They come back in the scene's own space, and the
        // `{ sphere }` path of useFlyTo expects world space, so flip the centre the same way the
        // `{ objectIds }` path does (selection basket does this too).
        const sphere = node.data.object.bounds?.sphere;
        if (sphere) {
            void flyTo({ sphere: isGlSpace(scene.up) ? { ...sphere, center: flip(sphere.center) } : sphere });
            close();
            return;
        }
        setIsBusy(true);
        try {
            await flyTo({ objectIds: [node.data.object.id], abortSignal: abortController.current.signal });
        } finally {
            setIsBusy(false);
        }
        close();
    };

    const actionStyle: React.CSSProperties = {
        width: 80,
        alignSelf: "stretch",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 4,
        border: "none",
        background: "rgba(172, 181, 185, 0.32)",
        cursor: "pointer",
        padding: 0,
        opacity: isBusy ? 0.5 : 1,
        pointerEvents: isBusy ? "none" : "auto",
    };

    const iconLabelStyle: React.CSSProperties = {
        fontSize: 14,
        fontWeight: 400,
        color: "#000000",
        fontFamily: "inherit",
        lineHeight: "18px",
    };

    return (
        <div style={{ position: "relative", overflow: "hidden" }}>
            <div style={{ position: "absolute", right: 0, top: 0, bottom: 0, display: "flex" }}>
                <button
                    type="button"
                    aria-label={t("zoomTo")}
                    onClick={(e) => void handleZoomTo(e)}
                    aria-hidden={!isRevealed}
                    tabIndex={isRevealed ? undefined : -1}
                    style={actionStyle}
                >
                    <ZoomToIcon style={{ width: 24, height: 24, color: "#000000" }} aria-hidden="true" />
                    <span style={iconLabelStyle}>{t("zoomTo")}</span>
                </button>
                <button
                    type="button"
                    aria-label={showUnisolate ? t("unisolate") : t("isolate")}
                    onClick={(e) => void handleIsolateToggle(e)}
                    aria-hidden={!isRevealed}
                    tabIndex={isRevealed ? undefined : -1}
                    style={actionStyle}
                >
                    <IsolateActionIcon style={{ width: 24, height: 24, color: "#000000" }} aria-hidden="true" />
                    <span style={iconLabelStyle}>{showUnisolate ? t("unisolate") : t("isolate")}</span>
                </button>
            </div>
            {/* Sliding content — white background covers the button until swiped */}
            <div ref={contentRef} style={{ position: "relative", zIndex: 1, background: "white", width: "100%" }}>
                {children}
            </div>
        </div>
    );
}
